/**
 * Verify Identity Step
 *
 * Third step in billing setup wizard - verifies billing identity with clearinghouse.
 * Shows verification status and provides retry functionality if needed.
 */

import { useEffect, useState } from 'react';
import { useBillingEntity, useBillingEntityOperations } from '@features/billing-entities';
import {
  describeNpiCheck,
  type NpiCheckNotice,
} from '@features/billing-entities/utils/npiCheckNotice';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { extractApiErrorMessage } from '../../utils/errorUtils';

interface SyncToStediStepProps {
  billingEntityId: string | null;
  onNext: () => void;
  onBack: () => void;
  onSyncCompleted: () => void;
}

export function SyncToStediStep({
  billingEntityId,
  onNext,
  onBack,
  onSyncCompleted,
}: SyncToStediStepProps) {
  const [syncAttempted, setSyncAttempted] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [npiCheckNotice, setNpiCheckNotice] = useState<NpiCheckNotice | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { entity, refetch: refetchEntity } = useBillingEntity(billingEntityId);
  const { sync, syncLoading, syncError, syncSuccess } = useBillingEntityOperations();

  // Auto-sync when step loads if entity exists
  useEffect(() => {
    if (billingEntityId && entity && !syncAttempted && !entity.stedi_synced) {
      handleSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingEntityId, entity]);

  const handleSync = async () => {
    if (!billingEntityId) return;

    setSyncAttempted(true);
    setLocalError(null);
    setNpiCheckNotice(null);

    try {
      const result = await sync({
        billingEntityId,
      }).unwrap();
      const notice = describeNpiCheck(result.npi_check);
      setNpiCheckNotice(notice);

      // Refetch entity to get updated sync status
      await refetchEntity();
      enqueueSnackbar(
        notice?.claimsAtRisk
          ? 'Billing identity verified.'
          : "Billing identity verified. You're ready to submit claims!",
        { variant: 'success' },
      );
      onSyncCompleted();
    } catch (err) {
      const errorMessage = extractApiErrorMessage(
        err,
        'Failed to verify billing identity. Please check your information and try again.',
      );
      setLocalError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
      console.error('Failed to sync billing entity:', err);
    }
  };

  const handleRetry = () => {
    handleSync();
  };

  const handleContinue = () => {
    onNext();
  };

  if (!billingEntityId || !entity) {
    return (
      <Box>
        <Alert severity="error">
          No billing identity found. Please go back and add a billing identity first.
        </Alert>
        <Button variant="outlined" onClick={onBack} fullWidth sx={{ mt: 2 }}>
          Back
        </Button>
      </Box>
    );
  }

  // Show success state if already synced
  const isSynced = entity.stedi_synced || syncSuccess;

  if (isSynced) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48, mr: 2 }} />
          <Box>
            <Typography variant="h6">Billing Identity Verified</Typography>
            {!npiCheckNotice?.claimsAtRisk && (
              <Typography variant="body2" color="text.secondary">
                You're ready to submit claims!
              </Typography>
            )}
          </Box>
        </Box>

        {npiCheckNotice && (
          <Alert severity={npiCheckNotice.severity} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {npiCheckNotice.title}
            </Typography>
            <Typography variant="body2">{npiCheckNotice.message}</Typography>
          </Alert>
        )}

        {entity.stedi_provider_id && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Clearinghouse Provider ID
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {entity.stedi_provider_id}
            </Typography>
          </Alert>
        )}

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Billing Identity Details:
          </Typography>
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Legal Name:</strong> {entity.legal_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>NPI:</strong> {entity.billing_npi}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Tax ID:</strong> {entity.tax_id_masked}
            </Typography>
            {entity.stedi_synced_at && (
              <Typography variant="body2" color="text.secondary">
                <strong>Verified:</strong>{' '}
                {new Date(entity.stedi_synced_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onBack} fullWidth>
            Back
          </Button>
          <Button variant="contained" onClick={handleContinue} fullWidth>
            Continue
          </Button>
        </Box>
      </Box>
    );
  }

  // Show loading state
  if (syncLoading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <CircularProgress size={48} sx={{ mr: 2 }} />
          <Box>
            <Typography variant="h6">Verifying your information...</Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a few moments
            </Typography>
          </Box>
        </Box>

        <LinearProgress sx={{ mb: 3 }} />

        <Alert severity="info">
          <Typography variant="body2">
            Please wait while we verify your billing identity with the clearinghouse. Do not close this window.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Show error state
  if (syncError || localError || (syncAttempted && !isSynced && !syncLoading)) {
    const displayError = localError || extractApiErrorMessage(
      syncError,
      'Failed to sync billing entity to Stedi. Please check your Stedi connection and try again.',
    );

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <ErrorIcon color="error" sx={{ fontSize: 48, mr: 2 }} />
          <Box>
            <Typography variant="h6">Verification Failed</Typography>
            <Typography variant="body2" color="text.secondary">
              There was a problem verifying your billing identity
            </Typography>
          </Box>
        </Box>

        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Error Details:
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {displayError}
          </Typography>
        </Alert>

        {/* Show entity details so user can see what might need fixing */}
        {entity && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Billing Identity Information:
            </Typography>
            <Typography variant="body2">
              <strong>Legal Name:</strong> {entity.legal_name}
            </Typography>
            <Typography variant="body2">
              <strong>NPI:</strong> {entity.billing_npi}
            </Typography>
            <Typography variant="body2">
              <strong>Tax ID:</strong> {entity.tax_id_masked}
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={onBack} fullWidth>
            Back (Edit Identity)
          </Button>
          <Button
            variant="contained"
            onClick={handleRetry}
            startIcon={<SyncIcon />}
            fullWidth
          >
            Retry Verification
          </Button>
        </Box>
      </Box>
    );
  }

  // Initial state - ready to verify
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Verify Billing Identity
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} component="p">
        We'll verify your information with the clearinghouse. This only takes a moment.
      </Typography>

      <Box sx={{ my: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          What's being verified:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Legal Name:</strong> {entity.legal_name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>NPI:</strong> {entity.billing_npi}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Tax ID:</strong> {entity.tax_id_masked}
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          The verification process registers your billing identity with the clearinghouse. This is
          required for electronic claim submission.
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={onBack} fullWidth>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSync}
          startIcon={<SyncIcon />}
          fullWidth
        >
          Verify & Finish Setup
        </Button>
      </Box>
    </Box>
  );
}
